import { ClearOutlined } from '@mui/icons-material';
import { IconButton, InputAdornment, TextField } from '@mui/material';
import { useEffect, useRef, useState } from 'react';

import { dateUtils } from '../../utils/dateUtils';

export interface timeInputState {
  name?: string;
  value?: string | null;
  seconds?: number | null;
}

interface onInputChangeOptions {
  preventOnChange?: boolean;
  setDefault?: boolean;
}
interface Iprops {
  labelText: string;
  name: string;
  onTimeInputChange: (o: timeInputState) => void;
  isSimple?: boolean;
  defaultValue?: string;
  zeroOnEmpty?: boolean;
  hasError?: boolean;
  resetValue?: boolean;
  disabled?: boolean;
  placeholder?: string;
  allowClear?: boolean;
}
const TimeInput = ({
  labelText,
  name,
  onTimeInputChange,
  isSimple = false,
  defaultValue = null,
  zeroOnEmpty = false,
  hasError = false,
  resetValue = false,
  disabled = false,
  placeholder = null,
  allowClear = false,
}: Iprops) => {
  if (zeroOnEmpty !== true) zeroOnEmpty = false;

  const inputRef = useRef<HTMLInputElement | null>(null);
  const [value, setValue] = useState('');
  const [myPlaceholder, setMyPlaceholder] = useState(placeholder || (isSimple ? 'mm:ss' : 'hh:mm:ss'));

  // Load the startup live clip on load if set
  useEffect(() => {
    // init code here
    init();
  }, []);
  useEffect(() => {
    // reload
    init();
  }, [defaultValue]);

  useEffect(() => {
    if (resetValue) {
      setValue('');
    }
  }, [resetValue]);

  const init = async () => {
    if (defaultValue != null) {
      const formattedValue = dateUtils.formatDuration(defaultValue, isSimple ? 'mm:ss' : 'hh:mm:ss');
      setValue(formattedValue);
    }
  };

  const onBlur = (e) => {
    onInputChange(e.target.value, { setDefault: true });
  };

  const _handleKeyDown = (e) => {
    setValue(e.target.value);
    if (e.key === 'Backspace') {
      e.preventDefault();
      onInputChange('', { preventOnChange: true });
    }
    if (e.key === 'Enter') {
      onInputChange(e.target.value);
    }
    // if (e.key === 'Tab') {
    //   e.preventDefault();
    //   onInputChange(e.target.value);
    // }
  };

  const handleClick = (e) => {
    onInputChange(e);
  };
  const onInputChange = (value, options: onInputChangeOptions) => {
    const parsedData = parseInputValue(value, options);
    if ((!options || !options.preventOnChange) && onTimeInputChange) {
      onTimeInputChange({ name, value: parsedData.formattedValue, seconds: parsedData.seconds });
    }
    if (!parsedData.formattedValue) {
      setValue('');
    } else {
      setValue(parsedData.formattedValue);
    }
  };

  // This function takes any string, and returns the correctly formatted string
  // and its numeric equivalent in seconds
  // e.g. '4:79' -> { formattedValue: '05:19', seconds: 379 }
  //        '79' -> { formattedValue: '01:19', seconds: 79 }
  //          '' -> { formattedValue: '00:00', seconds: 0 }
  //     '59:34' -> { formattedValue: '59:34', seconds: 3574 }
  //       '215' -> { formattedValue: '02:15', seconds: 135 }
  //        null -> { formattedValue: null, seconds: null}
  const parseInputValue = (val, options: onInputChangeOptions = null) => {
    // console.log('parseInputValue', { val, zeroOnEmpty });
    let parsedData = { formattedValue: null, seconds: null };
    if (val == null) {
      if (zeroOnEmpty) {
        return { formattedValue: isSimple ? '00:00' : '00:00:00', seconds: 0 };
      }
      return parsedData;
    }
    const myVal = cleanInputValue(val);
    if (!myVal) {
      if (options && options.setDefault && zeroOnEmpty) {
        return { formattedValue: isSimple ? '00:00' : '00:00:00', seconds: 0 };
      }
      return parsedData;
    }
    let hourPart = '00';
    let minutePart = '00';
    let secondPart = '00';
    let hours = 0;
    let minutes = 0;
    let seconds = 0;
    let totalSeconds = 0;
    const colonIndex = myVal.indexOf(':');
    if (colonIndex > -1) {
      // find the minutes and seconds from the string
      const hms = myVal.split(':');
      if (hms.length === 3) {
        hourPart = hms[0];
        minutePart = hms[1];
        secondPart = hms[2];
      } else {
        minutePart = hms[0];
        secondPart = hms[1];
      }
    } else if (myVal.length > 2) {
      minutePart = myVal.slice(0, myVal.length - 2);
      secondPart = myVal.slice(myVal.length - 2, myVal.length);
    } else {
      secondPart = myVal;
    }
    hours = Math.floor(parseInt(hourPart || '00'));
    minutes = Math.floor(parseInt(minutePart || '00'));
    seconds = Math.floor(parseInt(secondPart || '00'));
    if (seconds > 59) {
      minutes += Math.floor(seconds / 60);
      seconds %= 60;
    }
    if (minutes > 99) {
      hours += Math.floor(minutes / 60);
      minutes = seconds % 60;
    }
    if (hours > 99) {
      hours = 99;
    }
    totalSeconds = hours * 3600 + minutes * 60 + seconds;
    let formattedValue = dateUtils.formatDuration(totalSeconds);

    if (isSimple) {
      formattedValue = dateUtils.formatDuration(totalSeconds, 'mm:ss');
    }
    parsedData = { formattedValue, seconds: totalSeconds };
    return parsedData;
  };
  // This will strip away all non-numeric values as well as any colons except the first.
  const cleanInputValue = (val) => {
    let result = '';
    let lastCharIsColon = false;
    const stringVal = val.toString();
    for (let i = 0; i < stringVal.length; i++) {
      const c = stringVal[i];
      const charCode = stringVal.charCodeAt(i);
      if (c == ':') {
        // First semicolon allowed
        if (!lastCharIsColon) {
          lastCharIsColon = true;
          result += c;
        }
      } else {
        lastCharIsColon = false;
        if (charCode >= 48 && charCode <= 57) {
          result += c;
        } else if (charCode == 46) {
          // decimal point ok but will round down later
          result += c;
        }
      }
    }
    return result;
  };

  return (
    <TextField
      className="fuel-time-input"
      variant="outlined"
      onBlur={onBlur}
      onKeyDown={_handleKeyDown}
      onChange={_handleKeyDown}
      label={labelText}
      fullWidth
      size="small"
      value={value}
      error={hasError}
      placeholder={myPlaceholder}
      disabled={disabled}
      InputProps={
        allowClear && value && !disabled
          ? {
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    size="small"
                    className="fuel-time-input-clear"
                    onClick={() => onInputChange('', { setDefault: true })}
                    aria-label="Clear"
                    edge="end"
                  >
                    <ClearOutlined fontSize="small" />
                  </IconButton>
                </InputAdornment>
              ),
            }
          : undefined
      }
    />
  );
};

export default TimeInput;
